import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import queryString from "query-string";
import dayjs from "dayjs";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper/modules";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { Box, Chip, Stack } from "@mui/material";

// Components
import { userState, lastListCookieId, briefInfoType } from "../../interface/MainInterface";
import { HttpUnAuthApi } from "../../interface/unauth-api";
import { CommonUtils } from "../../utils/common_utils";
import LoadingCircle from "../../utils/LoadingCircle";
import { useWindowSize } from "../../utils/useWindowSize";
import PopAddScrap from "../scrap/PopAddScrap";
import BriefSkeleton from "./component/BriefSkeleton";

// CSS
import "./Brief.css";
import "swiper/css";
import BriefSlider from "./BriefSlider";
import SearchBrief from "./SearchBrief";

interface propsType {
  userState: userState;
}

const unAuthAPI = new HttpUnAuthApi();
const cUtils = new CommonUtils();

const Brief = (props: propsType) => {
  const { pk, sk } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams: any = queryString.parse(location.search);
  const slideRef: any = useRef();
  const windowSize = useWindowSize();
  const popScrapRef: any = useRef();

  const [isLoading, setIsLoading] = useState(false);
  const [breifList, setBreifList] = useState<any>([]);
  const [alikeList, setAlikeList] = useState<any>([]);
  const [briefInfo, setBreifInfo] = useState<briefInfoType | null>(null);

  const [prevSeq, setPrevSeq] = useState("");
  const [nextSeq, setNextSeq] = useState("");

  const [selectedImage, setSelectedImage] = useState<string>();
  const [isAtStart, setIsAtStart] = useState(true); // 슬라이드가 처음에 있는지
  const [isAtEnd, setIsAtEnd] = useState(false); // 슬라이드가 끝에 있는지
  const prevRef = useRef<HTMLDivElement>(null);
  const nextRef = useRef<HTMLDivElement>(null);
  const [swiperInstance, setSwiperInstance] = useState<any>(null); // Swiper 인스턴스를 저장

  const [isModalOpen, setIsModalOpen] = useState(false); // 모달 열림 상태
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [imageWidth, setImageWidth] = useState(0);
  const [imageHeight, setImageHeight] = useState(0);

  const [tmpKeyFlag, setTmpKeyFlag] = useState(false);
  const tPrefix = "3479812398712";
  const tSuffix = "4298381203831";
  const tKey = `${tPrefix}${props.userState.id}${tSuffix}`;

  const stateInfo: URLSearchParams = new URLSearchParams(
    window.location.search
  );

  useEffect(() => {
    // console.log("Brief page loaded with user state:", stateInfo.get("tKey"), props.userState);
    if (stateInfo.get("tKey")) {
      fnUserCount();
      setTmpKeyFlag(true);
    }

    if (!props.userState.isAuth) window.location.href = "/";
    return () => {
      setIsLoading(false);
      setAlikeList([]);
      setBreifInfo(null);
      setPrevSeq("");
      setNextSeq("");
    };
  }, []);

  useEffect(() => {
    checkLastList();
    get_breif_info();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pk, sk]);

  const fnUserCount = async () => {
    const param: any = {
      command: "update_user_count",
      user_id: "0000000", // 하트 공유용 주소로 접근한 사용자
    };

    const res = await unAuthAPI.post(param);
    if (res.code === "200") {
    }
  };

  const checkLastList = () => {
    // 쿠키에서 마지막 브리프 리스트를 조회 (이전, 다음 게시물 이동에 사용)
    const lastList = window.localStorage.getItem(lastListCookieId);
    setBreifList(lastList?.split(","));
  };

  const get_breif_info = async () => {
    // 이미지 슬라이더 갱신 이슈
    setBreifInfo(null);

    const param: any = {
      command: "get_brief_list_detail2",
      category: pk,
      briefSeq: sk,
    };

    setIsLoading(true);
    setAlikeList([]);
    const res = await unAuthAPI.post(param);
    if (res.code === "200") {
      setBreifInfo(res.response.brief_info);
    } else {
      console.error("get_breif_info : ", res.response.error_msg);
    }
    setIsLoading(false);
  };

  //비슷한 브리프 리스트 가져오기
  const get_alike_brief_list = async () => {
    const keywords = briefInfo?.ai_keyword;

    let temp: any = [];
    let shuffled: any = [];
    for (const keyword of keywords) {
      if (temp.length >= 8) break;
      // 1주일
      const from_dtm = dayjs().subtract(2, "week").format("YYYYMMDD");
      const to_dtm = dayjs().add(1, "day").format("YYYYMMDD");

      const param: any = {
        command: "get_brief_search_list",
        keyword: keyword,
        from_dtm: from_dtm,
        to_dtm: to_dtm,
      };

      const res = await unAuthAPI.post(param);
      if (res.code === "200") {
        // 최대 4개만 저장

        shuffled = [...res.response.brief_list]
          .filter((alike) => !(alike.pk === briefInfo?.pk && alike.sk === briefInfo?.sk))
          .sort(() => 0.5 - Math.random())
          .slice(0, 8);
        // for (const alike of res.response.brief_list) {
        //   if (temp.length >= 4) break;
        //   if (alike.pk === briefInfo?.pk && alike.sk === briefInfo?.sk) continue;
        //   temp.push(alike);
        // }
      } else {
        console.error("get_alike_brief_list : ", res.response.error_msg);
      }
    }

    setAlikeList(shuffled);
  };

  const handleCopy = useCallback(() => {
    const url = `${window.location.origin}/brief/${pk}/${sk}?tKey=${tKey}`;
    navigator.clipboard.writeText(url).then(() => {
      alert("주소가 복사되었습니다.");
    });
  }, [pk, sk, tKey]);

  useEffect(() => {
    window.scrollTo({
      top: 0, // 설정한 위치로 이동
      behavior: "smooth", // 부드럽게 스크롤
    });
    console.log(briefInfo);

    if (briefInfo?.ai_keyword !== undefined) {
      // 비슷한 브리프 가져오기
      // get_alike_brief_list();
    }

    if (briefInfo?.image_list.length > 0) {
      // console.log("1234");
      setSelectedImage(briefInfo?.image_list[0]);
    }
  }, [briefInfo]);

  useEffect(() => {
    setPrevSeq("");
    setNextSeq("");
    const idx = breifList.indexOf(`${pk}/${sk}`);
    if (idx < 0) return;
    if (idx - 1 > -1) setPrevSeq(breifList[idx - 1]);
    if (idx + 1 < breifList.length) setNextSeq(breifList[idx + 1]);
  }, [breifList]);

  const goPrev = () => {
    if (queryParams.result) navigate(`/brief/${prevSeq}?result=${queryParams.result}`);
    else navigate(`/brief/${prevSeq}?list`);
  };

  const goList = () => {
    if (queryParams.result) navigate(`/search?key=${queryParams.result}`);
    else navigate("/");
  };

  const goNext = () => {
    if (queryParams.result) navigate(`/brief/${nextSeq}?result=${queryParams.result}`);
    else navigate(`/brief/${nextSeq}?list`);
  };

  // 전체 보기 버튼 클릭 시 모달 열기
  const handleViewAllClick = (imgUrl: string) => {
    setIsModalOpen(true);
    setSelectedImage(imgUrl);
  };

  // 모달 외부 클릭 시 모달 닫기
  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleSlideChange = (swiper: any) => {
    setIsAtStart(swiper.isBeginning); // 슬라이드가 첫 번째면 true
    setIsAtEnd(swiper.isEnd); // 슬라이드가 마지막이면 true
  };

  const handleImageLoad = () => {
    if (imgRef.current) {
      setImageWidth(imgRef.current.clientWidth); // 이미지가 로드된 후 너비 설정
      setImageHeight(imgRef.current.clientHeight);
    }
  };

  return (
    <>
      {briefInfo ? (
        <section id="commWrap" className="brief">
          <article className="article-01">
            <div className="brief-wrap">
              <Box
                sx={{
                  width: windowSize.width > 1023 ? "calc(100% - 80px)" : "100%", // 양쪽 360px 여백
                  maxWidth: "1200px",
                  margin: "0 auto", // 가운데 정렬
                  padding: windowSize.width > 1023 ? "20px" : "0px", // 콘텐츠 간 간격
                }}
              >
                <div className="fake-height-81" />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" }, // 작은 화면에서는 세로 정렬, 큰 화면에서는 가로 정렬
                    // justifyContent: 'space-between',
                    alignItems: { md: "flex-start" }, // 큰 화면에서 위쪽 정렬
                  }}
                >
                  {/* 왼쪽: 상품 이미지들 */}
                  <Box
                    sx={{
                      // flex: '3',
                      mb: { xs: 3, md: 0 }, // 작은 화면에서 간격 추가
                      textAlign: "center",
                      flexGrow: 0,
                      width: { xs: "100%", md: "auto" },
                      height: "auto",
                      maxWidth: "50%",
                    }}
                  >
                    <div className="pc-img-wrap">
                      <BriefSlider image_list={briefInfo.image_list} />
                    </div>
                  </Box>
                  <Box sx={{ position: "absolute", left: 0, right: 0 }} className="mobile-img-wrap">
                    {briefInfo?.image_list.length > 0 && (
                      <div className="main-slide-wrap mobile-img-wrap">
                        <div className="swiper_center">
                          <Swiper
                            ref={slideRef}
                            slidesPerView={"auto"}
                            centeredSlides={true}
                            loop
                            spaceBetween={windowSize.width > 1023 ? 25 : 12}
                            navigation={{
                              nextEl: ".swiper-button-next",
                              prevEl: ".swiper-button-prev",
                            }}
                            autoplay={{
                              delay: 4000,
                              disableOnInteraction: false,
                            }}
                            modules={[Autoplay]}
                            className="swiper-wrapper"
                          >
                            {briefInfo?.image_list.map((imgUrl: string, index: number) => (
                              <SwiperSlide key={`brief-slide-${index}`} className="swiper-slide aitb-swiper-slide">
                                <img
                                  loading="lazy"
                                  src={imgUrl}
                                  alt=""
                                  onError={(e) => {
                                    e.currentTarget.src = "/images/no-image.png";
                                    e.currentTarget.className = "noImage";
                                  }}
                                />
                                {/* 전체 보기 버튼을 대표 이미지 오른쪽 위에 추가 */}
                                <button
                                  className="btn-full-screen"
                                  onClick={() => {
                                    handleViewAllClick(imgUrl);
                                  }}
                                  style={{
                                    position: "absolute",
                                    top: "16px", // 위쪽 여백
                                    // left: '370px', // 오른쪽 여백
                                    right: "16px",
                                    // backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                    border: "none",
                                    borderRadius: "5px",
                                    width: "40px",
                                    height: "40px",
                                    backgroundColor: "rgba(0, 0, 0, 0.2)",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <img src="/images/btn-full-screen-20.png" alt="" />
                                </button>
                              </SwiperSlide>
                            ))}
                          </Swiper>
                        </div>
                      </div>
                    )}
                  </Box>
                  {briefInfo?.image_list.length > 0 && <div className="main-slide-wrap mobile-img-wrap fake-height-320"></div>}
                  {/* 오른쪽: 상품 설명 */}
                  <Box
                    sx={{
                      // flex: 'auto',
                      flexGrow: 1,
                      textAlign: "left",
                    }}
                  >
                    <div className="brief-cont" style={{ width: "100%", maxWidth: "100%" }}>
                      <div
                        className="brief-text"
                        style={{ width: "auto", padding: "0px", margin: windowSize.width > 1023 ? "0px 0px 0px 80px" : "0px" }}
                      >
                        <div className="brief-info" style={{ margin: "0px auto 25px" }}>
                          <Stack direction={"row"} gap={2}>
                            {props.userState.isSuperAdmin === true && (
                              <span style={{ color: "#7A7A7A" }}>
                                <Stack direction="row" sx={{ display: "flex", alignItems: "center" }} gap={1}>
                                  <i>
                                    <img
                                      src={cUtils.genRefSiteLogo(briefInfo.ref_site)}
                                      onClick={() => {
                                        window.open(briefInfo.ref_site, "_blank");
                                      }}
                                      alt=""
                                    />
                                  </i>
                                </Stack>
                              </span>
                            )}
                            {tmpKeyFlag ? (
                              <button
                                className="org-news-btn"
                                onClick={() => {
                                  navigate(`/article/${pk}/${sk}?tKey=${tKey}`);
                                }}
                                style={{ width: windowSize.width > 375 ? "120px" : "100px" }}
                              >
                                {windowSize.width > 375 ? "원본기사 보기 ↗" : "원본기사 ↗"}
                              </button>
                            ) : (
                              <>
                                <button
                                  className="org-news-btn"
                                  onClick={() => {
                                    navigate(`/article/${pk}/${sk}`);
                                  }}
                                  style={{ width: windowSize.width > 375 ? "120px" : "100px" }}
                                >
                                  {windowSize.width > 375 ? "원본기사 보기 ↗" : "원본기사 ↗"}
                                </button>

                                {props.userState.isSuperAdmin === true && (
                                  <button
                                    className="org-news-btn"
                                    onClick={handleCopy}
                                    style={{
                                      width: windowSize.width > 375 ? "148px" : "100px",
                                      backgroundColor: "white",
                                      color: "black",
                                    }}
                                  >
                                    H-ART용 주소 복사
                                  </button>
                                )}
                              </>
                            )}
                          </Stack>
                          <ul style={{ width: "auto" }}>
                            <li className="upload-date" style={{ marginRight: "0px" }}>
                              {dayjs(briefInfo?.sk).format("YYYY-MM-DD HH:mm")}
                            </li>
                          </ul>
                        </div>
                        <dl>
                          <dt>{briefInfo?.ai_title}</dt>
                          {briefInfo?.ai_introduction !== undefined &&
                            briefInfo?.ai_introduction !== "" &&
                            briefInfo?.ai_introduction !== null && (
                              <dd className="white-space-pre-line underline bold">{briefInfo?.ai_introduction + "\n\n"}</dd>
                            )}
                          <dd className="white-space-pre-line">
                            <div className="markdown">
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>{briefInfo?.ai_report}</ReactMarkdown>
                            </div>
                          </dd>
                        </dl>
                      </div>

                      <div
                        className="brief-insight"
                        style={{
                          width: "auto",
                          maxWidth: "100%",
                          padding: "0px",
                          margin: windowSize.width > 1023 ? "0px 0px 0px 80px" : "0px",
                        }}
                      >
                        <dl>
                          <dt>
                            <span className="highlighter half">인사이트 도출</span>
                            <img src="/images/icon-insight.png" alt="" />
                          </dt>
                          {briefInfo?.ai_insight.map((insight: any, index: number) => (
                            <dd key={`ai-insight-${index}`} className="white-space-pre-line">
                              <div className="markdown">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{insight}</ReactMarkdown>
                              </div>
                            </dd>
                          ))}
                        </dl>
                      </div>
                    </div>
                    {/* 기타 상품 정보들 */}
                  </Box>
                </Box>

                {/* 아래: 비슷한 상품 추천 보기 */}
                {/* <Box sx={{ mt: 4 }}>
                  <div className="brief-cont">
                    {alikeList.length > 0 && (
                      <div className="rel-brief" style={{ padding: "0px", margin: "0px", width: "100%", maxWidth: "100%" }}>
                        <dl style={{ borderTop: "0px" }}>
                          <dt>
                            다른 Brief 보기
                            <img src="/images/icon-look.png" alt="" />
                            <div style={{ width: "24px" }} />
                            
                            <div className="swiper-navigation">
                              <div ref={prevRef} className="custom-prev-arrow">
                                <img
                                  src="/images/icon-arr-prev-dark.png"
                                  alt=""
                                  style={{
                                    opacity: isAtStart ? 0.2 : 1,
                                    cursor: "pointer",
                                    pointerEvents: swiperInstance ? "auto" : "none",
                                  }}
                                />
                              </div>
                              <div ref={nextRef} className="custom-next-arrow">
                                <img
                                  src="/images/icon-arr-next-dark.png"
                                  alt=""
                                  style={{
                                    opacity: isAtEnd ? 0.2 : 1,
                                    cursor: "pointer",
                                    pointerEvents: swiperInstance ? "auto" : "none",
                                  }}
                                />
                              </div>
                            </div>
                          </dt>
                          <dd style={{ flexWrap: "nowrap" }}>
                            <Swiper
                              slidesPerView={"auto"}
                              spaceBetween={25.8}
                              navigation={{
                                prevEl: prevRef.current,
                                nextEl: nextRef.current,
                              }}
                              modules={[Navigation]}
                              onSwiper={(swiper: any) => {
                                setSwiperInstance(swiper);
                                if (swiper.params.navigation) {
                                  const navigation = swiper.params.navigation as any;
                                  navigation.prevEl = prevRef.current;
                                  navigation.nextEl = nextRef.current;
                                  // swiper.navigation.init();
                                  swiper.navigation.update();
                                }
                              }}
                              style={{ zIndex: 0, width: "100%" }}
                              onSlideChange={handleSlideChange} // 슬라이드 변경 시 상태 업데이트
                            >
                              {alikeList.map((data: briefInfoType, index: number) => (
                                <SwiperSlide
                                  key={`alike-list-${index}`}
                                  style={{ width: "258px", borderRadius: "0px", height: "100%", alignItems: "start", display: "block" }}
                                >
                                  <div
                                    key={`alike-list-${index}`}
                                    className="brief-box simple"
                                    onClick={() => {
                                      window.open(`/brief/${data.pk}/${data.sk}`);
                                    }}
                                    style={{ width: "100%" }}
                                  >
                                    <div className="thumb-img">
                                      {data.image_list.length > 0 ? (
                                        <img
                                          src={data.image_list[0]}
                                          alt=""
                                          onError={(e) => {
                                            e.currentTarget.src = "/images/no-image.png";
                                            e.currentTarget.className = "noImage";
                                          }}
                                        />
                                      ) : (
                                        <img src="/images/no-image.png" className="noImage" />
                                      )}
                                    </div>
                                    <div className="brief-summary">
                                      <dl>
                                        <dt style={{ marginBottom: "8px" }}>
                                          <p
                                            style={{
                                              whiteSpace: "nowrap", // 한 줄로 유지
                                              overflow: "hidden", // 넘치는 부분 숨김
                                              textOverflow: "ellipsis", // 넘치는 부분에 '...' 표시
                                              display: "block",
                                              maxWidth: "100%",
                                            }}
                                          >
                                            {data.ai_title}
                                          </p>
                                        </dt>
                                        
                                        <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
                                          {data.ai_keyword.map((keyword: string, index: number) => (
                                            <Chip
                                              key={index}
                                              label={"#" + keyword}
                                              sx={{
                                                '& .MuiChip-label': {
                                                  paddingLeft: "8px",
                                                  paddingRight: "8px",
                                                },
                                                color: "#8a8a8a", // 글자 색상
                                                backgroundColor: "#fff", // Chip 안의 배경 색상
                                                border: "1px solid #d1d1d1", // 테두리 색상
                                                fontSize: "0.875rem",
                                                fontWeight: 400,
                                                height: "28px",
                                              }}
                                            />
                                          ))}
                                        </div>
                                      </dl>
                                    </div>
                                  </div>
                                </SwiperSlide>
                              ))}
                              
                              {!isAtStart && <div className="swiper-gradient-left"></div>}

                              {!isAtEnd && <div className="swiper-gradient-right"></div>}
                            </Swiper>
                          </dd>
                        </dl>
                      </div>
                    )}
                  </div>
                </Box> */}

                <SearchBrief 
                  keyword={briefInfo?.title.trim()} 
                  searchList={briefInfo?.search_list}
                />
              </Box>
            </div>
            <div className="breif-footer-margin"></div>
            <div className="mobile-footer-margin"></div>
          </article>
          {(queryParams.list === null || queryParams.result) && (
            <div id="bottomNav" className="nav-bar-wrap">
              <div className="nav-btns pc">
                {prevSeq !== "" ? (
                  <button className="prev-post" onClick={goPrev}>
                    ← 이전 Brief
                  </button>
                ) : (
                  <div></div>
                )}

                <button className="go-list" onClick={goList}>
                  <i className="list-icon"></i>리스트로 돌아가기
                </button>
                {nextSeq !== "" ? (
                  <button className="next-post" onClick={goNext}>
                    다음 Brief →
                  </button>
                ) : (
                  <div></div>
                )}
              </div>
              <div className="nav-btns mobile">
                {prevSeq !== "" ? (
                  <button className="prev-post" onClick={goPrev}>
                    ←
                  </button>
                ) : (
                  <div></div>
                )}
                <button className="go-list" onClick={goList}>
                  <i className="list-icon"></i>리스트로 돌아가기
                </button>
                {nextSeq !== "" ? (
                  <button className="next-post" onClick={goNext}>
                    →
                  </button>
                ) : (
                  <div></div>
                )}
              </div>
            </div>
          )}
        </section>
      ) : (
        <BriefSkeleton />
      )}

      {isModalOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: 'blur(8px)',
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
          onClick={handleModalClose} // 모달 외부 클릭 시 닫기
        >
          <img
            src={selectedImage}
            ref={imgRef}
            onLoad={handleImageLoad}
            alt="전체 이미지"
            style={{
              maxWidth: "85%",
              maxHeight: "85%",
            }}
          />

          {/* 전체 보기 버튼을 대표 이미지 오른쪽 위에 추가 */}
          <button
            onClick={handleModalClose}
            style={{
              position: "absolute",
              top: `calc(50% - ${imageHeight / 2 - 16}px)`, // 위쪽 여백
              right: `calc(50% - ${imageWidth / 2 - 16}px)`, // 오른쪽 여백
              // backgroundColor: 'rgba(255, 255, 255, 0.8)',
              border: "none",
              borderRadius: "5px",
              width: "40px",
              height: "40px",
              backgroundColor: "rgba(0, 0, 0, 0.2)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img src="/images/btn-minimize-screen-20.png" alt="" />
          </button>
        </Box>
      )}
      <LoadingCircle loading={isLoading} />
      <PopAddScrap ref={popScrapRef} userState={props.userState} selectedKeywords={[]} callback={(payload: any) => {}} />
    </>
  );
};

export default Brief;
